import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CircleNotchIcon } from '@phosphor-icons/react';
import { UserCompanyRole, type UserCompanyMemberDto } from '@monorepo/shared';
import { BaseModal } from '../ui/BaseModal';
import { FormInput } from '../ui/FormInput';
import { useGetCompanyGroups, useAddMembersToGroup } from '../../services/groups.service';
import { useInviteUser, useUpdateUserRole } from '../../services/users.service';
import { EMAIL_REGEX } from '../../constants/validation';

interface UserModalProps {
  companyId: string;
}

export interface UserModalRef {
  openInvite: () => void;
  openEdit: (user: UserCompanyMemberDto) => void;
}

interface UserFormData {
  firstName: string;
  lastName: string;
  email: string;
  role: UserCompanyRole;
}

export const UserModal = forwardRef<UserModalRef, UserModalProps>(({ companyId }, ref) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<'invite' | 'edit'>('invite');
  const [selectedUser, setSelectedUser] = useState<UserCompanyMemberDto | null>(null);
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [allGroups, setAllGroups] = useState<
    Array<{ id: string; name: string; memberCount: number }>
  >([]);
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);

  const {
    register,
    handleSubmit: handleFormSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<UserFormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      role: UserCompanyRole.SUPER_USER,
    },
  });

  const inviteUserMutation = useInviteUser(companyId);
  const updateUserRoleMutation = useUpdateUserRole(companyId);
  const addMembersToGroupMutation = useAddMembersToGroup(companyId);

  const { data: groupsData, isLoading: isGroupsLoading } = useGetCompanyGroups(
    companyId,
    currentPage,
    10,
  );

  useImperativeHandle(ref, () => ({
    openInvite: () => {
      setMode('invite');
      setSelectedUser(null);
      setIsOpen(true);
    },
    openEdit: (user: UserCompanyMemberDto) => {
      setMode('edit');
      setSelectedUser(user);
      setIsOpen(true);
    },
  }));

  useEffect(() => {
    if (groupsData?.items) {
      setAllGroups((prev) => {
        const existingGroupsMap = new Map(prev.map((g) => [g.id, g]));
        groupsData.items.forEach((g) => {
          existingGroupsMap.set(g.id, g);
        });
        return Array.from(existingGroupsMap.values());
      });
    }
  }, [groupsData]);

  useEffect(() => {
    if (mode === 'edit' && selectedUser) {
      setValue('firstName', selectedUser.firstName);
      setValue('lastName', selectedUser.lastName);
      setValue('email', selectedUser.email);
      setValue('role', selectedUser.role);
      setSelectedGroupIds(new Set(selectedUser?.groupIds || []));
    } else {
      reset();
      setSelectedGroupIds(new Set());
    }
    setCurrentPage(1);
  }, [mode, selectedUser, isOpen, setValue, reset]);

  const canLoadMoreGroups = !!groupsData && currentPage < groupsData.totalPages;

  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel || !canLoadMoreGroups) return undefined;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !isGroupsLoading) {
        setCurrentPage((prev) => prev + 1);
      }
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [canLoadMoreGroups, isGroupsLoading, isOpen]);

  const handleSubmit = async (data: UserFormData) => {
    try {
      let userCompanyId: string;

      if (mode === 'invite') {
        const newUser = await inviteUserMutation.mutateAsync({
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          role: data.role,
        });
        userCompanyId = newUser.id;
      } else if (mode === 'edit' && selectedUser) {
        await updateUserRoleMutation.mutateAsync({
          userCompanyId: selectedUser.id,
          data: {
            role: data.role,
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
          },
        });
        userCompanyId = selectedUser.id;
      } else {
        return;
      }

      if (selectedGroupIds.size > 0) {
        await Promise.all(
          Array.from(selectedGroupIds).map((groupId) =>
            addMembersToGroupMutation.mutateAsync({
              groupId,
              memberIds: [userCompanyId],
            }),
          ),
        );
      }

      handleClose();
    } catch (error) {
      console.error('Error submitting user modal:', error);
    }
  };

  const handleClose = () => {
    reset();
    setSelectedGroupIds(new Set());
    setCurrentPage(1);
    setIsOpen(false);
  };

  const toggleGroupSelection = (groupId: string) => {
    setSelectedGroupIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const modalKey = mode === 'invite' ? 'invite_modal' : 'edit_modal';
  const isLoading =
    inviteUserMutation.isPending ||
    updateUserRoleMutation.isPending ||
    addMembersToGroupMutation.isPending;

  const footer = (
    <div className="flex gap-4">
      <button
        type="button"
        onClick={handleClose}
        className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition"
        disabled={isLoading}
      >
        {t('admin.users.invite_modal.cancel')}
      </button>
      <button
        type="submit"
        form="user-form"
        className="flex-1 px-4 py-2 bg-primary text-white rounded-md hover:bg-opacity-90 transition disabled:opacity-50"
        disabled={isLoading}
      >
        {t(`admin.users.${modalKey}.submit`)}
      </button>
    </div>
  );

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      title={t(`admin.users.${modalKey}.title`)}
      footer={footer}
      maxWidth="2xl"
    >
      <form id="user-form" onSubmit={handleFormSubmit(handleSubmit)} className="space-y-6">
        <FormInput
          label={t('admin.users.invite_modal.first_name.label')}
          type="text"
          placeholder={t('admin.users.invite_modal.first_name.placeholder')}
          error={errors.firstName?.message}
          required
          {...register('firstName', {
            required: t('admin.users.invite_modal.first_name.error.required'),
          })}
        />

        <FormInput
          label={t('admin.users.invite_modal.last_name.label')}
          type="text"
          placeholder={t('admin.users.invite_modal.last_name.placeholder')}
          error={errors.lastName?.message}
          required
          {...register('lastName', {
            required: t('admin.users.invite_modal.last_name.error.required'),
          })}
        />

        <FormInput
          label={t('admin.users.invite_modal.email.label')}
          type="email"
          placeholder={t('admin.users.invite_modal.email.placeholder')}
          error={errors.email?.message}
          required
          disabled={mode === 'edit'}
          {...register('email', {
            required: t('admin.users.invite_modal.email.error.required'),
            pattern: {
              value: EMAIL_REGEX,
              message: t('admin.users.invite_modal.email.error.invalid'),
            },
          })}
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            {t('admin.users.invite_modal.role.label')}
            <span className="text-red-500 ml-1">*</span>
          </label>
          <div className="space-y-3">
            <label className="flex items-start cursor-pointer">
              <input
                type="radio"
                value={UserCompanyRole.SUPER_USER}
                {...register('role', {
                  required: t('admin.users.invite_modal.role.error.required'),
                })}
                className="w-5 h-5 text-primary mt-0.5 focus:ring-primary"
              />
              <div className="ml-3">
                <div className="font-medium text-gray-900">
                  {t('admin.users.invite_modal.role.super_user')}
                </div>
                <div className="text-sm text-gray-500">
                  {t('admin.users.invite_modal.role.super_user_description')}
                </div>
              </div>
            </label>

            <label className="flex items-start cursor-pointer">
              <input
                type="radio"
                value={UserCompanyRole.VALIDATOR}
                {...register('role', {
                  required: t('admin.users.invite_modal.role.error.required'),
                })}
                className="w-5 h-5 text-primary mt-0.5 focus:ring-primary"
              />
              <div className="ml-3">
                <div className="font-medium text-gray-900">
                  {t('admin.users.invite_modal.role.validator')}
                </div>
                <div className="text-sm text-gray-500">
                  {t('admin.users.invite_modal.role.validator_description')}
                </div>
              </div>
            </label>

            <label className="flex items-start cursor-pointer">
              <input
                type="radio"
                value={UserCompanyRole.USER}
                {...register('role', {
                  required: t('admin.users.invite_modal.role.error.required'),
                })}
                className="w-5 h-5 text-primary mt-0.5 focus:ring-primary"
              />
              <div className="ml-3">
                <div className="font-medium text-gray-900">
                  {t('admin.users.invite_modal.role.user')}
                </div>
                <div className="text-sm text-gray-500">
                  {t('admin.users.invite_modal.role.user_description')}
                </div>
              </div>
            </label>
          </div>
          {errors.role && <p className="mt-1 text-sm text-red-500">{errors.role.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('admin.users.invite_modal.groups.label')}
          </label>
          <p className="text-sm text-gray-500 mb-3">
            {t('admin.users.invite_modal.groups.description')}
          </p>
          <div className="border border-gray-300 rounded-md">
            {allGroups.length === 0 && !isGroupsLoading && (
              <div className="p-4 text-center text-gray-500 text-sm">
                {t('admin.users.invite_modal.groups.empty')}
              </div>
            )}
            {allGroups.map((group) => (
              <label
                key={group.id}
                className="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
              >
                <input
                  type="checkbox"
                  checked={selectedGroupIds.has(group.id)}
                  onChange={() => toggleGroupSelection(group.id)}
                  className="w-4 h-4 text-primary bg-white border-gray-300 rounded focus:ring-primary"
                />
                <div className="ml-3 flex-1">
                  <div className="text-sm font-medium text-gray-900">{group.name}</div>
                  <div className="text-xs text-gray-500">
                    {group.memberCount} {t('admin.users.invite_modal.groups.members')}
                  </div>
                </div>
              </label>
            ))}
            {isGroupsLoading && (
              <div className="flex justify-center items-center p-4">
                <CircleNotchIcon size={20} className="animate-spin text-primary" />
              </div>
            )}
            <div ref={loadMoreSentinelRef} />
          </div>
          {selectedGroupIds.size > 0 && (
            <p className="text-sm text-gray-600 mt-2">
              {t('admin.users.invite_modal.groups.selected', { count: selectedGroupIds.size })}
            </p>
          )}
        </div>
      </form>
    </BaseModal>
  );
});

UserModal.displayName = 'UserModal';
